import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AIChallenges = () => {
  const challenges = [
    {
      number: 1,
      title: "Overwhelm & Confusion",
      description: "With so many AI tools and platforms available, it's easy to feel lost about where to start and which solutions are right for your business."
    },
    {
      number: 2,
      title: "Implementation Struggles",
      description: "Many businesses struggle to effectively integrate AI into their existing workflows and processes, leading to wasted resources."
    },
    {
      number: 3,
      title: "Lack of Strategy",
      description: "Without a clear AI strategy aligned with business goals, companies often make costly mistakes or fail to see meaningful results."
    },
    {
      number: 4,
      title: "Skill Gaps",
      description: "Teams often lack the necessary skills to leverage AI effectively, creating bottlenecks and missed opportunities."
    },
    {
      number: 5,
      title: "Access to AI Resources",
      description: "With AI evolving rapidly and new tools emerging constantly, many struggle to find reliable, up-to-date resources to learn and implement AI effectively."
    },
    {
      number: 6,
      title: "Cost Considerations",
      description: "While some AI solutions require significant investment, more affordable and accessible AI tools are now available."
    }
  ];

  return (
    <ChallengesSection>
      <div className="container">
        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          The AI Challenge
        </motion.h2>
        <IntroWrapper
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <IntroText>
            Businesses & individuals face several obstacles when trying to learn & implement AI solutions.
          </IntroText>
          <IntroText>
            Here are the most common challenges we help overcome:
          </IntroText>
        </IntroWrapper>
        
        <ChallengesGrid>
          {challenges.map((challenge, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
            >
              <ChallengeCard>
                <ChallengeNumber>{challenge.number}</ChallengeNumber>
                <h3>{challenge.title}</h3>
                <p>{challenge.description}</p>
              </ChallengeCard>
            </motion.div>
          ))}
        </ChallengesGrid>
      </div>
    </ChallengesSection>
  );
};

const ChallengesSection = styled.section`
  background-color: var(--white);
  text-align: center;
`;

const IntroWrapper = styled(motion.div)`
  max-width: 920px;
  margin: 0 auto;
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ChallengesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ChallengeCard = styled.div`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  text-align: left;
  box-shadow: var(--shadow);
  position: relative;
  transition: var(--transition);
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ChallengeNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  
  &:nth-child(1) {
    background-color: #fee2e2;
    color: var(--red);
  }
  
  ${ChallengeCard}:nth-child(1) & {
    background-color: #fee2e2;
    color: var(--red);
  }
  
  ${ChallengeCard}:nth-child(2) & {
    background-color: #ffedd5;
    color: var(--red);
  }
  
  ${ChallengeCard}:nth-child(3) & {
    background-color: #fef9c3;
    color: var(--yellow);
  }
  
  ${ChallengeCard}:nth-child(4) & {
    background-color: #dcfce7;
    color: var(--green);
  }
  
  ${ChallengeCard}:nth-child(5) & {
    background-color: #dbeafe;
    color: #1d4ed8;
  }
  
  ${ChallengeCard}:nth-child(6) & {
    background-color: #f3e8ff;
    color: #7e22ce;
  }
`;

export default AIChallenges;
